/**
 * ============================================================================
 * KbOutlinePanel — Painel lateral "Outline" (índice de headings), estilo
 *                  GitHub. Filtro por texto, indentação por nível, destaque
 *                  do heading visível (scroll-spy) e clique para rolar.
 *
 * Uso: recebe o `contentJson` do editor + o container rolável
 *      (`scrollContainer`) onde estão os headings renderizados.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Robustece scrollTo (busca por id + fallback
 *            data-kb-heading-id), memoiza observer por lista de ids,
 *            atualiza hash sem rolar, remove associação por índice (agora
 *            os ids vêm do plugin ProseMirror HeadingIdExtension).
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Filter, X, List as ListIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDocHeadings, extractHeadings } from "@/hooks/kb/useDocHeadings";

interface Props {
  contentJson: unknown;
  /** Elemento rolável onde os headings renderizados existem (ex.: kb-prose). */
  scrollContainer?: HTMLElement | null;
  /** Callback opcional para fechar o painel via botão X. */
  onClose?: () => void;
}

/**
 * Localiza o heading dentro do container aceitando tanto `id` (padrão) quanto
 * `data-kb-heading-id` (compatibilidade com decoração ProseMirror caso algum
 * outro plugin sobrescreva o `id`).
 */
function findHeadingEl(root: HTMLElement, id: string): HTMLElement | null {
  try {
    const byId = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (byId) return byId;
  } catch { /* ignore: id inválido para seletor */ }
  return root.querySelector<HTMLElement>(`[data-kb-heading-id="${id}"]`);
}

export function KbOutlinePanel({ contentJson, scrollContainer, onClose }: Props) {
  const headings = useDocHeadings(contentJson);
  const [filter, setFilter] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return headings;
    return headings.filter((h) => h.text.toLowerCase().includes(q));
  }, [headings, filter]);

  // Chave estável para o observer: só recria quando a lista real de ids muda,
  // não a cada tecla digitada no editor.
  const idsKey = useMemo(() => headings.map((h) => h.id).join("|"), [headings]);

  // Scroll-spy: destaca o heading visível mais próximo do topo.
  useEffect(() => {
    if (!scrollContainer) return;
    const ids = idsKey ? idsKey.split("|") : [];
    if (ids.length === 0) return;

    const targets = ids
      .map((id) => findHeadingEl(scrollContainer, id))
      .filter((el): el is HTMLElement => !!el);
    if (targets.length === 0) return;

    let raf = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const visible = entries
            .filter((e) => e.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) {
            const el = visible[0].target as HTMLElement;
            const id = el.id || el.getAttribute("data-kb-heading-id");
            if (id) setActiveId(id);
          }
        });
      },
      {
        root: scrollContainer,
        rootMargin: "0px 0px -70% 0px",
        threshold: [0, 1],
      },
    );
    targets.forEach((el) => observer.observe(el));
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [scrollContainer, idsKey]);

  const scrollTo = (id: string) => {
    if (!scrollContainer) return;
    const el = findHeadingEl(scrollContainer, id);
    if (!el) return;
    // Calcula offset relativo ao container rolável (não à janela) — funciona
    // mesmo com wrappers/blocos custom entre o heading e o scroll container.
    const containerRect = scrollContainer.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const top = elRect.top - containerRect.top + scrollContainer.scrollTop - 12;
    scrollContainer.scrollTo({ top, behavior: "smooth" });
    setActiveId(id);
    // Atualiza hash sem forçar scroll do browser (deep-link / share).
    try {
      const url = new URL(window.location.href);
      url.hash = id;
      window.history.replaceState(window.history.state, "", url.toString());
    } catch { /* ignore */ }
  };

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <div className="flex items-center gap-2">
          <ListIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Outline
          </span>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose} aria-label="Fechar outline">
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="px-3 py-2 border-b">
        <div className="relative">
          <Filter className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar títulos"
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {headings.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground text-center">
            Adicione títulos ao artigo para gerar o índice.
          </div>
        )}
        {headings.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
            Nenhum título encontrado.
          </div>
        )}
        {filtered.map((h) => {
          const active = activeId === h.id;
          const indent = Math.max(0, h.level - 1) * 12;
          return (
            <button
              key={h.id}
              type="button"
              onClick={() => scrollTo(h.id)}
              className={`block w-full text-left text-xs py-1 pr-2 border-l-2 transition-colors ${
                active
                  ? "border-primary bg-accent/60 text-foreground font-medium"
                  : "border-transparent hover:bg-accent/40 text-muted-foreground hover:text-foreground"
              }`}
              style={{ paddingLeft: 12 + indent }}
              title={h.text}
            >
              <span className="line-clamp-2 leading-snug">{h.text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Exporta helper de extração para uso no reader (que não precisa do painel).
export { extractHeadings };
